import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, Hash, Phone, Sparkles, User } from 'lucide-react';
import axios from 'axios';
import { trackLead } from '../utils/metaPixel';

const gold = '#C9A961';

const DRIVER_TRAITS = {
  1: { title: 'The Leader', brief: 'Independent, ambitious, original. Natural-born leaders who carve their own path.' },
  2: { title: 'The Diplomat', brief: 'Sensitive, cooperative, intuitive. Excel in partnerships and emotional intelligence.' },
  3: { title: 'The Creative', brief: 'Expressive, optimistic, social. Born communicators with artistic talent.' },
  4: { title: 'The Builder', brief: 'Disciplined, practical, hard-working. Build lasting foundations through persistence.' },
  5: { title: 'The Explorer', brief: 'Versatile, restless, freedom-loving. Thrive on change, travel, and new experiences.' },
  6: { title: 'The Nurturer', brief: 'Responsible, caring, harmonious. Natural healers focused on family and beauty.' },
  7: { title: 'The Thinker', brief: 'Analytical, spiritual, introspective. Deep seekers of truth and hidden knowledge.' },
  8: { title: 'The Powerhouse', brief: 'Authoritative, ambitious, karmic. Destined for material success through hard work.' },
  9: { title: 'The Humanitarian', brief: 'Compassionate, generous, global. Driven to serve humanity and complete life cycles.' },
};

const FAMOUS_NAMES = {
  1: ['Akshay Kumar (28=1)', 'Deepika Padukone (28=1)', 'Rajinikanth (37=1)', 'APJ Abdul Kalam (28=1)'],
  2: ['MS Dhoni (20=2)', 'Priyanka Chopra (47=2)', 'Mother Teresa (47=2)'],
  3: ['Salman Khan (30=3)', 'Mukesh Ambani (39=3)', 'Mahatma Gandhi (39=3)', 'Ekta Kapoor (30=3)', 'Kareena Kapoor (39=3)'],
  4: ['Aamir Khan (22=4)', 'Alia Bhatt (13=4)', 'Sundar Pichai (40=4)', 'Gautam Adani (31=4)'],
  5: ['Narendra Modi (41=5)', 'Virat Kohli (32=5)', 'Ratan Tata (23=5)', 'Sachin Tendulkar (50=5)', 'Dhirubhai Ambani (41=5)'],
  6: ['Shah Rukh Khan (42=6)', 'Shahrukh Khan (42=6)'],
  7: ['Amitabh Bachchan (43=7)', 'Aishwarya Rai (43=7)', 'Hrithik Roshan (52=7)', 'Nita Ambani (34=7)', 'Ranveer Singh (52=7)'],
  8: ['Rohit Sharma (35=8)'],
  9: ['Ajay Devgn (27=9)'],
};

const CALCULATORS = [
  { id: 'name', label: 'Name Number', icon: User },
  { id: 'dc', label: 'Driver & Conductor', icon: Hash },
  { id: 'mobile', label: 'Lucky Mobile', icon: Phone },
];

export default function HomeCalculators({ onCTA }) {
  const [activeCalc, setActiveCalc] = useState('name');
  const [nameInput, setNameInput] = useState('');
  const [dobInput, setDobInput] = useState('');
  const [dcNameInput, setDcNameInput] = useState('');
  const [mobileInput, setMobileInput] = useState('');
  const [mobileDobInput, setMobileDobInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatDOB = (value) => {
    const digits = value.replace(/\D/g, '');
    let f = '';
    if (digits.length > 0) f = digits.substring(0, 2);
    if (digits.length >= 3) f += '-' + digits.substring(2, 4);
    if (digits.length >= 5) f += '-' + digits.substring(4, 8);
    return f;
  };

  const resetResult = () => setResult(null);

  const calcNameNumber = async () => {
    if (!nameInput.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/public/name-number', { name: nameInput.trim() });
      setResult({ type: 'name', data: res.data });
      trackLead('name_number_calculator');
    } catch (e) { setResult({ type: 'error', message: e.response?.data?.detail || 'Error' }); }
    finally { setLoading(false); }
  };

  const calcDC = async () => {
    if (!dobInput || dobInput.length < 10) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/public/driver-conductor', { birth_date: dobInput, name: dcNameInput.trim() });
      setResult({ type: 'dc', data: res.data });
      trackLead('driver_conductor_calculator');
    } catch (e) { setResult({ type: 'error', message: e.response?.data?.detail || 'Error' }); }
    finally { setLoading(false); }
  };

  const calcMobile = async () => {
    if (!mobileInput || mobileInput.length < 10 || !mobileDobInput || mobileDobInput.length < 10) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/public/lucky-mobile', { mobile: mobileInput, birth_date: mobileDobInput });
      setResult({ type: 'mobile', data: res.data });
      trackLead('lucky_mobile_calculator');
    } catch (e) { setResult({ type: 'error', message: e.response?.data?.detail || 'Error' }); }
    finally { setLoading(false); }
  };

  return (
    <section id="free-calculators" className="py-10 md:py-14 px-4 md:px-5 relative" style={{ background: 'linear-gradient(180deg, #0A0F2A 0%, #0D1229 50%, #101632 100%)' }}>
      {/* Decorative golden glow behind the section */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201,169,97,0.06) 0%, transparent 70%)' }} />

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6 md:mb-8">
          <span className="inline-block px-6 py-2.5 rounded-full text-sm md:text-base font-bold tracking-widest uppercase mb-5"
            style={{ color: '#0A0F2A', background: `linear-gradient(135deg, ${gold}, #E8D5A3)`, boxShadow: '0 0 30px rgba(201,169,97,0.35)' }}>
            Try It Free — Instant Results
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Discover Your <span style={{ color: gold }}>Numbers</span> Right Now
          </h2>
          <p className="text-slate-400 text-sm md:text-base">Select a calculator below. No sign-up needed.</p>
        </motion.div>

        {/* Calculator Tabs */}
        <div className="flex justify-center gap-2 md:gap-3 mb-5 md:mb-6 flex-wrap" data-testid="calc-tabs">
          {CALCULATORS.map(c => {
            const Icon = c.icon;
            const active = activeCalc === c.id;
            return (
              <button key={c.id} data-testid={`calc-tab-${c.id}`}
                onClick={() => { setActiveCalc(c.id); resetResult(); }}
                className="flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: active ? 'rgba(201,169,97,0.12)' : 'rgba(255,255,255,0.03)',
                  border: active ? `2px solid ${gold}` : '1px solid rgba(255,255,255,0.08)',
                  color: active ? gold : '#64748B',
                  boxShadow: active ? '0 0 20px rgba(201,169,97,0.15)' : 'none',
                }}>
                <Icon size={16} /> {c.label}
              </button>
            );
          })}
        </div>

        {/* Calculator Input Area — Premium golden border */}
        <motion.div layout className="rounded-2xl p-5 md:p-8 relative"
          style={{
            background: 'linear-gradient(135deg, rgba(10,15,42,0.95), rgba(16,22,50,0.9))',
            border: '2px solid rgba(201,169,97,0.35)',
            boxShadow: '0 0 40px rgba(201,169,97,0.08), inset 0 1px 0 rgba(201,169,97,0.1)',
          }}>
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-8 h-8 pointer-events-none" style={{ borderTop: `3px solid ${gold}`, borderLeft: `3px solid ${gold}`, borderRadius: '12px 0 0 0' }} />
          <div className="absolute top-0 right-0 w-8 h-8 pointer-events-none" style={{ borderTop: `3px solid ${gold}`, borderRight: `3px solid ${gold}`, borderRadius: '0 12px 0 0' }} />
          <div className="absolute bottom-0 left-0 w-8 h-8 pointer-events-none" style={{ borderBottom: `3px solid ${gold}`, borderLeft: `3px solid ${gold}`, borderRadius: '0 0 0 12px' }} />
          <div className="absolute bottom-0 right-0 w-8 h-8 pointer-events-none" style={{ borderBottom: `3px solid ${gold}`, borderRight: `3px solid ${gold}`, borderRadius: '0 0 12px 0' }} />

          <AnimatePresence mode="wait">
            {/* NAME NUMBER */}
            {activeCalc === 'name' && (
              <motion.div key="name" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h3 className="text-white text-lg font-semibold mb-1">Calculate Your Name Number</h3>
                <p className="text-slate-400 text-sm mb-5">Enter your full name as you commonly use it</p>
                <div className="flex gap-3">
                  <input data-testid="calc-name-input" type="text" value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && calcNameNumber()}
                    placeholder="Enter your name"
                    className="flex-1 px-4 py-3 rounded-lg text-white text-sm"
                    style={{ background: 'rgba(10,15,42,0.8)', border: '1px solid rgba(201,169,97,0.25)', borderRadius: '12px' }} />
                  <button data-testid="calc-name-btn" onClick={calcNameNumber} disabled={loading || !nameInput.trim()}
                    className="px-6 py-3 rounded-lg font-semibold text-sm disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #C9A961, #E8D5A3)', color: '#0A0F2A', boxShadow: '0 0 20px rgba(201,169,97,0.3)' }}>
                    {loading ? '...' : 'Calculate'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* DRIVER & CONDUCTOR */}
            {activeCalc === 'dc' && (
              <motion.div key="dc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h3 className="text-white text-lg font-semibold mb-1">Calculate Your Driver & Conductor</h3>
                <p className="text-slate-400 text-sm mb-5">Your birth numbers reveal your core personality blueprint</p>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <input data-testid="calc-dob-input" type="text" value={dobInput}
                      onChange={(e) => setDobInput(formatDOB(e.target.value))}
                      placeholder="Date of Birth (DD-MM-YYYY)" maxLength={10}
                      className="flex-1 px-4 py-3 rounded-lg text-white text-sm"
                      style={{ background: 'rgba(10,15,42,0.8)', border: '1px solid rgba(201,169,97,0.25)', borderRadius: '12px' }} />
                    <input data-testid="calc-dc-name-input" type="text" value={dcNameInput}
                      onChange={(e) => setDcNameInput(e.target.value)}
                      placeholder="Your Name (optional)"
                      className="flex-1 px-4 py-3 rounded-lg text-white text-sm"
                      style={{ background: 'rgba(10,15,42,0.8)', border: '1px solid rgba(201,169,97,0.25)', borderRadius: '12px' }} />
                  </div>
                  <button data-testid="calc-dc-btn" onClick={calcDC} disabled={loading || dobInput.length < 10}
                    className="w-full px-6 py-3 rounded-lg font-semibold text-sm disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #C9A961, #E8D5A3)', color: '#0A0F2A', boxShadow: '0 0 20px rgba(201,169,97,0.3)' }}>
                    {loading ? 'Calculating...' : 'Reveal My Numbers'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* LUCKY MOBILE */}
            {activeCalc === 'mobile' && (
              <motion.div key="mobile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h3 className="text-white text-lg font-semibold mb-1">Is Your Mobile Number Lucky?</h3>
                <p className="text-slate-400 text-sm mb-5">Check if your mobile number aligns with your birth energy</p>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <input data-testid="calc-mobile-input" type="tel" value={mobileInput}
                      onChange={(e) => setMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile number" maxLength={10}
                      className="flex-1 px-4 py-3 rounded-lg text-white text-sm"
                      style={{ background: 'rgba(10,15,42,0.8)', border: '1px solid rgba(201,169,97,0.25)', borderRadius: '12px' }} />
                    <input data-testid="calc-mobile-dob-input" type="text" value={mobileDobInput}
                      onChange={(e) => setMobileDobInput(formatDOB(e.target.value))}
                      placeholder="DOB (DD-MM-YYYY)" maxLength={10}
                      className="flex-1 px-4 py-3 rounded-lg text-white text-sm"
                      style={{ background: 'rgba(10,15,42,0.8)', border: '1px solid rgba(201,169,97,0.25)', borderRadius: '12px' }} />
                  </div>
                  <button data-testid="calc-mobile-btn" onClick={calcMobile}
                    disabled={loading || mobileInput.length < 10 || mobileDobInput.length < 10}
                    className="w-full px-6 py-3 rounded-lg font-semibold text-sm disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #C9A961, #E8D5A3)', color: '#0A0F2A', boxShadow: '0 0 20px rgba(201,169,97,0.3)' }}>
                    {loading ? 'Checking...' : 'Check My Number'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RESULT AREA */}
          <AnimatePresence>
            {result && result.type !== 'error' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(201,169,97,0.2)' }}>

                {/* NAME NUMBER RESULT */}
                {result.type === 'name' && (
                  <div data-testid="calc-result-name">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold"
                          style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '2px solid #EF4444' }}>
                          {result.data.compound}
                        </div>
                        <span className="text-slate-400 text-xl">=</span>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold"
                          style={{ background: 'rgba(201,169,97,0.15)', color: gold, border: `2px solid ${gold}` }}>
                          {result.data.name_number}
                        </div>
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">Your Name Number is {result.data.name_number}</p>
                        <p className="text-slate-400 text-sm">{result.data.name} → Compound {result.data.compound} → Final {result.data.name_number}</p>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(201,169,97,0.06)', border: '1px solid rgba(201,169,97,0.15)' }}>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Many celebrities have found success by aligning their name number with their birth energy.
                        {FAMOUS_NAMES[result.data.name_number] && (
                          <> Famous personalities with number {result.data.name_number}: <strong style={{ color: gold }}>{FAMOUS_NAMES[result.data.name_number].join(', ')}</strong>.</>
                        )}
                        {' '}Is your name vibrating in harmony with your destiny? A small correction could unlock bigger results.
                      </p>
                    </div>
                    <button onClick={onCTA} data-testid="calc-cta-name"
                      className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                      style={{ background: gold, color: '#0A0F2A' }}>
                      Get Your Name Corrected — Just ₹1,499 <ArrowRight size={16} />
                    </button>
                    <p className="text-center text-slate-500 text-xs mt-2">Includes FREE Astrology + Vastu + Weekly Insight — lifetime access</p>
                  </div>
                )}

                {/* DRIVER & CONDUCTOR RESULT */}
                {result.type === 'dc' && (
                  <div data-testid="calc-result-dc">
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1 rounded-xl p-4 text-center" style={{ background: 'rgba(0,217,255,0.08)', border: '1px solid rgba(0,217,255,0.25)' }}>
                        <p className="text-slate-400 text-xs mb-1">DRIVER (Psychic)</p>
                        <p className="text-3xl font-bold" style={{ color: '#00D9FF' }}>{result.data.driver}</p>
                        <p className="text-white text-sm font-semibold mt-1">{DRIVER_TRAITS[result.data.driver]?.title}</p>
                      </div>
                      <div className="flex-1 rounded-xl p-4 text-center" style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)' }}>
                        <p className="text-slate-400 text-xs mb-1">CONDUCTOR (Destiny)</p>
                        <p className="text-3xl font-bold" style={{ color: '#7C3AED' }}>{result.data.conductor}</p>
                        <p className="text-white text-sm font-semibold mt-1">{DRIVER_TRAITS[result.data.conductor]?.title}</p>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 mb-3" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <p className="text-slate-300 text-sm leading-relaxed mb-2">
                        <strong style={{ color: '#00D9FF' }}>Driver {result.data.driver}:</strong> {DRIVER_TRAITS[result.data.driver]?.brief}
                      </p>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        <strong style={{ color: '#7C3AED' }}>Conductor {result.data.conductor}:</strong> {DRIVER_TRAITS[result.data.conductor]?.brief}
                      </p>
                    </div>
                    {result.data.name_number !== undefined && (
                      <div className="rounded-lg p-3 mb-3" style={{
                        background: result.data.name_resonates ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                        border: `1px solid ${result.data.name_resonates ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                      }}>
                        <p className="text-sm" style={{ color: result.data.name_resonates ? '#10B981' : '#EF4444' }}>
                          Your name number ({result.data.name_number}) {result.data.name_resonates ? 'resonates with' : 'does NOT resonate with'} your Driver/Conductor. {!result.data.name_resonates && 'A name correction could improve your outcomes.'}
                        </p>
                      </div>
                    )}
                    <button onClick={onCTA} data-testid="calc-cta-dc"
                      className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                      style={{ background: gold, color: '#0A0F2A' }}>
                      Get Full Lo Shu Grid Analysis — Just ₹1,499 <ArrowRight size={16} />
                    </button>
                    <p className="text-center text-slate-500 text-xs mt-2">Lifetime access with easy remedies + Astrology + Vastu + Weekly Insight</p>
                  </div>
                )}

                {/* MOBILE NUMBER RESULT */}
                {result.type === 'mobile' && (
                  <div data-testid="calc-result-mobile">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center text-lg font-bold"
                        style={{
                          background: result.data.verdict === 'LUCKY' ? 'rgba(16,185,129,0.15)' : result.data.verdict === 'OKAY' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                          color: result.data.verdict === 'LUCKY' ? '#10B981' : result.data.verdict === 'OKAY' ? '#F59E0B' : '#EF4444',
                          border: `2px solid ${result.data.verdict === 'LUCKY' ? '#10B981' : result.data.verdict === 'OKAY' ? '#F59E0B' : '#EF4444'}`,
                        }}>
                        {result.data.vibration}
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">
                          {result.data.verdict === 'LUCKY' ? 'This number is LUCKY for you!' : result.data.verdict === 'OKAY' ? 'This number is OKAY for you' : 'This number is NOT LUCKY for you'}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {result.data.mobile} = {result.data.total} = vibration {result.data.vibration} | Your Driver: {result.data.driver}, Conductor: {result.data.conductor}
                        </p>
                      </div>
                    </div>
                    {result.data.compound && (
                      <div className="rounded-lg p-3 mb-3" style={{ background: 'rgba(201,169,97,0.06)', border: '1px solid rgba(201,169,97,0.15)' }}>
                        <p className="text-sm text-slate-300">
                          <strong style={{ color: gold }}>Compound {result.data.compound.number} — {result.data.compound.name}</strong> ({result.data.compound.nature}): {result.data.compound.meaning}
                        </p>
                      </div>
                    )}
                    <div className="rounded-xl p-4 mb-3" style={{
                      background: result.data.verdict === 'NOT LUCKY' ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)',
                      border: `1px solid ${result.data.verdict === 'NOT LUCKY' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)'}`,
                    }}>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {result.data.verdict === 'LUCKY'
                          ? 'Great match! This mobile number resonates with your birth energy. Communications and opportunities through this number flow naturally.'
                          : result.data.verdict === 'OKAY'
                          ? 'Acceptable but not optimal. This number is friendly to your birth energy but not a perfect match.'
                          : 'This number creates resistance with your birth energy. Calls, messages, and opportunities may face friction. Consider getting a number aligned with your Driver or Conductor.'}
                      </p>
                    </div>
                    <button onClick={onCTA} data-testid="calc-cta-mobile"
                      className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                      style={{ background: gold, color: '#0A0F2A' }}>
                      Get Detailed Mobile Analysis + Lucky Number — ₹1,499 <ArrowRight size={16} />
                    </button>
                    <p className="text-center text-slate-500 text-xs mt-2">Includes full Numerology + Astrology + Vastu + Weekly Insight — lifetime</p>
                  </div>
                )}
              </motion.div>
            )}

            {result?.type === 'error' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                <p className="text-red-400 text-sm text-center">{result.message}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
